import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useStoreActions } from 'easy-peasy';
import { useReactToPrint } from 'react-to-print';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './css/pdf.css';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

//import tool
import { dateFormat, getQuantityDisplay, renderOrDash } from '../../tools/manager';

// ✅ import du logo SVG et encodage Base64
import LogoFlex from '../../../assets/images/svg/logo-flex-sacarabany-sarl.svg';

import { decodeAndNormalizeSignatureSvg } from "./tools/signature";
import base64 from 'react-native-base64';

const PdfPreIntervCoupon = () => {
  // init navigation
  const navigate = useNavigate();
  const { idworkorder } = useParams();

  //  🌱🌱 init store(services)   🌱🌱
  const getPreCoupon = useStoreActions((actions) => actions.middleware.CouponPreInterMiddleware.getPreCoupon);

  //init state
  const [dataPdf, setDataPdf] = useState(null);
  const [mySvg, setMySvg] = useState('');
  const [logoBase64, setLogoBase64] = useState('');

  const componentRef = useRef();
  const handlePdf = useReactToPrint({
    contentRef: componentRef,
    content: () => componentRef.current,
    documentTitle: 'pre-intervention-coupon',
    onAfterPrint: () => toast.success('✅ PDF généré avec succès'),
  });

  // call bon pré-rempli
  const CallGetPreCoupon = async () => {
    try {
      const res = await getPreCoupon(idworkorder);
      setDataPdf(res);
      // setMySvg(res?.signed ? decodeAndNormalizeSignatureSvg(res.signed) : '');
      setMySvg(res?.signed ? base64.decode(res?.signed) : '');
    } catch (error) {
      toast.error('❌ Une erreur est survenue durant le chargement des données du bon');
    }
  };

  // first effect
  useEffect(() => {
    CallGetPreCoupon();

    // encodage du logo en base64
    fetch(LogoFlex)
      .then((res) => res.text())
      .then((svgText) => {
        const encoded = `data:image/svg+xml;base64,${btoa(svgText)}`;
        setLogoBase64(encoded);
      });
  }, []);

  const formattedDate = (updatedAt) => {
    const dateA = updatedAt ? parseISO(updatedAt) : new Date();
    return format(dateA, "EEEE dd MMMM yyyy 'à' HH:mm", {
      locale: fr,
    });
  };

  // ====== HELPERS TABLE ======

  const materials = dataPdf?.materials || [];

  const hasDepose = materials.some(
    (m) => m?.pose !== null && m?.pose !== undefined && String(m.pose).trim() !== ''
  );

  const hasSurface = materials.some(
    (m) => m?.surface !== null && m?.surface !== undefined && String(m.surface).trim() !== ''
  );
  const hasDepot = materials.some(
    (m) => m?.depot !== null && m?.depot !== undefined && String(m.depot).trim() !== ''
  );
  const hasComments = materials.some(
    (m) => m?.comments !== null && m?.comments !== undefined && String(m.comments).trim() !== ''
  );

  return (
    <section className="ml-32 mt-14 mb-10 flex space-x-7">
      {!!dataPdf && (
        <div className="shadow-2xl h-[1170px]">
          <div
            ref={componentRef}
            className="w-[827px] h-[1170px] rounded-lg"
          >
            {/* Bandeau orange */}
            <div className="bg-orange-clim rounded-lg text-sm text-orange-clim h-4" />

            <div className="flex flex-col pt-10 px-10 h-[98%]">
              {/* ✅ Logo + titre */}
              <div className="flex justify-between items-start mb-6">
                {logoBase64 && (
                  <img
                    src={logoBase64}
                    alt="Logo Sacarabany"
                    style={{ height: '60px', width: 'auto' }}
                  />
                )}
                <div className="text-right">
                  <h3 className="text-xl my-0 font-semibold">
                    Bon d'intervention pré rempli - N° {dataPdf?.numero || ''}
                  </h3>
                  <h3 className="text-base my-0 font-semibold">
                    Bon d’intervention CTM - N° {dataPdf?.ctm || ''}
                  </h3>
                </div>
              </div>

              {/* div infos main */}
              <div className="flex justify-between mt-5">
                {/* infos société */}
                <div className="mr-28 flex flex-col space-y-2">
                  <h4 className="font-bold">SACARABANY SARL</h4>
                  <div className="text-sm text-gray-file flex flex-col space-y-2">
                    <p>279 rue solitude la mulatresse</p>
                    <p>97200 Fort de France</p>
                    <p>SIRET : 495 262 131 00016</p>
                    <p>contact@sacarabany-sarl.fr</p>
                    <p>Créé le : {formattedDate(dataPdf?.createdAt)}</p>
                  </div>
                </div>
                {/* infos client */}
                <div className="flex flex-col space-y-2">
                  <h4>{dataPdf?.worksites?.company?.name?.toUpperCase() || ''}</h4>
                  <div className="text-sm text-gray-file flex flex-col space-y-2">
                    <p>{dataPdf?.worksites?.company?.address || ''}</p>
                    {dataPdf?.worksites?.company?.siret && <p>SIRET : {dataPdf?.worksites?.company?.siret}</p>}
                    <p>{dataPdf?.newMailClient || dataPdf?.worksites?.company?.mail?.split(',')[0] || ''}</p>
                    <p>{dataPdf?.worksites?.company?.phone || ''}</p>
                  </div>
                </div>
              </div>

              {/* chantier */}
              <div className="my-10 flex flex-col space-y-2">
                <h3 className="font-bold">Chantier : {dataPdf?.worksites?.name || ''}</h3>
                <p>Date de réalisation : {dataPdf?.dateSigned ? dateFormat(dataPdf?.dateSigned).full : '-'}</p>
                {dataPdf?.orderCoupon && (
                  <p className="font-bold ">Lié au bon de commande N° {dataPdf?.orderCoupon}</p>
                )}
              </div>

              {/* tableau matériaux */}
              <table className="tableContainer">
                <thead>
                  <tr>
                    <th>Nature</th>
                    <th>Matériel</th>
                    <th>Référence</th>
                    <th>Quantité</th>
                    {hasDepose && <th>Lieu de pose</th>}
                    {hasDepot && <th>Lieu de dépose</th>}
                    {hasSurface && <th>Surface</th>}
                    {hasComments && <th>Commentaire</th>}
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {materials.map((material, idx) => (
                    <tr key={idx}>
                      <td>{renderOrDash(material?.nature)}</td>
                      <td>{renderOrDash(material?.material)}</td>
                      <td>{renderOrDash(material?.refName)}</td>
                      <td>{getQuantityDisplay(material)}</td>
                      {hasDepose && <td>{renderOrDash(material?.pose)}</td>}
                      {hasDepot && <td>{renderOrDash(material?.depot)}</td>}
                      {hasSurface && <td>{renderOrDash(material?.surface)}</td>}
                      {hasComments && <td>{renderOrDash(material?.comments)}</td>}
                      <td>{material?.statut ? '✔️' : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="grow" />

              {/* signature */}
              <div className="text-right flex flex-col items-end justify-end">
                <p className="font-medium px-11">Signature :</p>
                {!!mySvg && (
                  <div
                    // className="signature-wrapper"
                    className="bg-cover"
                    dangerouslySetInnerHTML={{ __html: mySvg }}
                  />
                )}
              </div>

              {/* footer */}
              <div className=" text-center text-gray-file text-xs border-t border-gray-separate pt-5 pb-7">
                <h6>SACARABANY SARL SIRET : 495 262 131 00016 - APE : 453 A</h6>
                <p>279 rue solitude la mulatresse - 97200 Fort de France</p>
                <p>TEL : 0596 70 75 50 - 0696 45 02 08</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col justify-between text-blue-admin h-[1170px]">
        <div className="border border-blue-admin py-4 px-3 rounded-xl">
          <button onClick={() => navigate(-1)}>Voir tous les bons d'intervention</button>
        </div>
        <button
          className="flex-end bg-blue-admin text-white py-4 rounded-xl"
          onClick={handlePdf}
        >
          Générer un PDF
        </button>
      </div>
    </section>
  );
};

export default PdfPreIntervCoupon;
